import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Layers } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { refreshCatalogs } from "./MoneyMovementFields";
import GroupingSelect from "./GroupingSelect";
import { invalidateConceptCache } from "./ConceptSelect";

// ============================================================================
// V1.44 · CATÁLOGO MAESTRO — elementos reales (catalog_items)
// ============================================================================
export default function CatalogItemsSection({ kind, title, desc, testPrefix }) {
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const [groupId, setGroupId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const load = () => api.get("/catalog-items", { params: { kind } })
    .then((r) => setItems(r.data))
    .catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); }, [kind]);

  const afterChange = () => { invalidateConceptCache(kind); refreshCatalogs(); load(); };

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/catalog-items", { kind, name: name.trim(), group_id: groupId, active: true });
      toast.success("Elemento creado");
      setName(""); setGroupId(null);
      afterChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    try {
      await api.put(`/catalog-items/${editing.id}`, { kind, name: editing.name, group_id: editing.group_id || null, active: editing.active !== false });
      toast.success("Actualizado");
      setEditing(null);
      afterChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleActive = async (it, v) => {
    try {
      await api.put(`/catalog-items/${it.id}`, { kind, name: it.name, group_id: it.group_id || null, active: v });
      afterChange();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.name}" del Catálogo Maestro?`)) return;
    try {
      await api.delete(`/catalog-items/${it.id}`);
      toast.success("Eliminado");
      afterChange();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm(`${apiError(e)}\n\n¿Desvincular los movimientos y eliminar de todos modos? (Los movimientos históricos conservan su nombre y familia)`)) {
          try { await api.delete(`/catalog-items/${it.id}?force=true`); toast.success("Eliminado y desvinculado"); afterChange(); } catch (e2) { toast.error(apiError(e2)); }
        }
      } else toast.error(apiError(e));
    }
  };

  const filtered = useMemo(
    () => (items || []).filter((it) => !q || it.name.toLowerCase().includes(q.toLowerCase())),
    [items, q]
  );
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((it) => {
      const key = (it.group_path || []).length > 0 ? it.group_path.join(" › ") : "Sin agrupación";
      (map[key] = map[key] || []).push(it);
    });
    const keys = Object.keys(map).sort((a, b) => {
      if (a === "Sin agrupación") return 1;
      if (b === "Sin agrupación") return -1;
      return a.localeCompare(b, "es");
    });
    return { map, keys };
  }, [filtered]);

  return (
    <div className="border border-border rounded-lg p-5 space-y-4" data-testid={`${testPrefix}-section`}>
      <div>
        <h3 className="font-heading font-bold text-lg">{title}</h3>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px_auto] gap-2">
        <Input data-testid={`${testPrefix}-name-input`} placeholder="Nombre del elemento…" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} />
        <GroupingSelect kind={kind} value={groupId} onChange={setGroupId} testId={`${testPrefix}-group-select`} reloadKey={reloadKey} />
        <Button data-testid={`${testPrefix}-create-btn`} onClick={create} disabled={!name.trim()}><Plus size={15} className="mr-1" /> Crear</Button>
      </div>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid={`${testPrefix}-search`} className="pl-9" placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {items === null ? (
        <div className="p-4 text-sm text-muted-foreground">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">Sin elementos. Crea el primero arriba.</div>
      ) : (
        <div className="space-y-3">
          {grouped.keys.map((k) => (
            <div key={k} className="border border-border rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-secondary/40 text-xs font-semibold text-muted-foreground flex items-center gap-2">
                <Layers size={12} /> {k} <span className="opacity-60">({grouped.map[k].length})</span>
              </div>
              <div className="divide-y divide-border">
                {grouped.map[k].map((it) => (
                  <div key={it.id} className={`flex items-center justify-between px-3 py-2 hover:bg-secondary/30 ${it.active === false ? "opacity-60" : ""}`} data-testid={`${testPrefix}-row-${it.id}`}>
                    <span className="text-sm font-medium truncate">{it.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <button data-testid={`${testPrefix}-edit-${it.id}`} onClick={() => { setEditing({ ...it }); setReloadKey((x) => x + 1); }} className="text-muted-foreground hover:text-foreground"><Pencil size={14} /></button>
                      <Switch data-testid={`${testPrefix}-toggle-${it.id}`} checked={it.active !== false} onCheckedChange={(v) => toggleActive(it, v)} className="scale-90" />
                      <button data-testid={`${testPrefix}-delete-${it.id}`} onClick={() => remove(it)} className="text-muted-foreground hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-sm" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Editar elemento</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Input data-testid={`${testPrefix}-edit-name`} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} autoFocus />
              <div>
                <label className="text-xs text-muted-foreground">Agrupación</label>
                <GroupingSelect kind={kind} value={editing.group_id} onChange={(v) => setEditing({ ...editing, group_id: v })} testId={`${testPrefix}-edit-group`} reloadKey={reloadKey} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Activo</span>
                <Switch checked={editing.active !== false} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
              </div>
              <Button data-testid={`${testPrefix}-save`} className="w-full" onClick={save} disabled={!editing.name.trim()}>Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


